import { useRef } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client/react';

import {
    CreateCertificateDocument,
    DeleteCertificatesDocument,
    ICertificateEditFragment,
    IAppPermissionsCertificate,
    IContentTypeValue,
    IScoringInput,
    UpdateCertificateDocument,
    UpdateExtraCategoryValuesDocument,
} from 'graphql/types';
import { CertificateForm } from 'certificate/forms/CertificateForm/CertificateForm';
import { ExternalCertificateForm } from 'certificate/forms/ExternalCertificateForm/ExternalCertificateForm';
import { useApolloError } from 'common/hooks/useApolloError';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useExtraCategoryValues } from 'common/hooks/useExtraCategoryValues';
import { getExtraCategoryValues } from 'organisation/utils/extraCategory';
import { Loader } from 'common/components/Loader';
import {
    TCertificateFormValues,
    TExternalCertificateFormValues,
} from 'certificate/types';
import { useAppPermissions } from 'user/hooks/usePermissions';

interface IProps {
    certificate?: ICertificateEditFragment;
    isExternalCertificateCreatePage?: boolean;
    onClose?(): void;
}

export const CertificateFormController = ({
    certificate,
    isExternalCertificateCreatePage,
    onClose,
}: IProps) => {
    const [translate] = useTranslation();
    const { showApolloError } = useApolloError();
    const [displaySnackbar] = useSnackbar();
    const { canUpdate = false }: IAppPermissionsCertificate['management'] =
        useAppPermissions('certificate', 'management');
    const [createCertificate] = useMutation(CreateCertificateDocument);
    const [updateCertificate] = useMutation(UpdateCertificateDocument);
    const [deleteCertificates] = useMutation(DeleteCertificatesDocument);
    const [updateExtraCategoryValues] = useMutation(
        UpdateExtraCategoryValuesDocument
    );

    // Preserve the form type, set once on mount to prevent switching during drawer close
    const isExternalCertificate = useRef(
        isExternalCertificateCreatePage || certificate?.isExternalCertificate
    ).current;

    const {
        extraCategories,
        queryLoading: extraCategoriesLoading,
        handleUpdateExtraCategoryValues,
    } = useExtraCategoryValues({
        contentType: IContentTypeValue.Certificate,
        modelId: certificate?.id || '',
        typeNames: ['Certificate'],
    });

    const handleCreateCertificate = async (
        values: TCertificateFormValues | TExternalCertificateFormValues,
        isExternal = false
    ) => {
        const {
            reference,
            extraCategories: extraCategoryValues,
            ...rest
        } = values;

        let newCertificateId: string | undefined;

        try {
            const result = await createCertificate({
                variables: {
                    certificate: {
                        ...rest,
                        ref: reference,
                        scoring: values.scoring as IScoringInput | undefined,
                        isExternalCertificate: isExternal,
                    },
                },
                update: (cache) => {
                    cache.evict({ fieldName: 'certificates' });
                },
            });

            newCertificateId = result.data?.createCertificate?.certificate?.id;
        } catch (error) {
            showApolloError(error);

            return;
        }

        const newExtraCategoryValues = getExtraCategoryValues(
            extraCategoryValues || {},
            extraCategories
        );

        // The certificate must exist before its extra category values can be
        // saved, so they are persisted in a follow-up mutation after creation.
        if (newCertificateId && newExtraCategoryValues.length) {
            try {
                await updateExtraCategoryValues({
                    variables: {
                        contentType: IContentTypeValue.Certificate,
                        modelId: newCertificateId,
                        values: newExtraCategoryValues,
                    },
                    // Filter options are derived from existing values, so the
                    // newly-saved values may introduce new filter options.
                    refetchQueries: ['ExtraCategoryFilters'],
                    awaitRefetchQueries: true,
                });
            } catch {
                displaySnackbar(
                    translate(
                        'certificatesManagementPage.actionPartialSuccess.create'
                    ),
                    {
                        variant: 'warning',
                    }
                );

                onClose?.();

                return;
            }
        }

        displaySnackbar(
            translate('certificatesManagementPage.actionSuccess.create'),
            {
                variant: 'success',
            }
        );

        onClose?.();

        return;
    };

    const handleUpdateCertificate: SubmitHandler<
        TCertificateFormValues | TExternalCertificateFormValues
    > = async (values) => {
        if (!certificate?.id) return;

        const {
            reference,
            extraCategories: extraCategoryValues,
            ...rest
        } = values;

        try {
            await updateCertificate({
                variables: {
                    id: certificate.id,
                    certificate: {
                        ...rest,
                        ref: reference,
                        scoring: !!values.scoring?.score
                            ? (values.scoring as IScoringInput)
                            : null,
                    },
                },
                update: (cache) => {
                    cache.evict({ fieldName: 'certificates' });
                },
            });

            await handleUpdateExtraCategoryValues(
                certificate.extraCategoryValues,
                extraCategoryValues || {}
            );
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(
            translate('certificatesManagementPage.actionSuccess.update'),
            {
                variant: 'success',
            }
        );

        onClose?.();

        return;
    };

    const handleDeleteCertificate = async () => {
        if (!certificate?.id) return;

        try {
            await deleteCertificates({
                variables: {
                    ids: [certificate.id],
                },
                update: (cache) => {
                    cache.evict({ fieldName: 'certificates' });
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(
            translate('certificatesManagementPage.actionSuccess.delete'),
            {
                variant: 'success',
            }
        );

        onClose?.();

        return;
    };

    const FormComponent = isExternalCertificate
        ? ExternalCertificateForm
        : CertificateForm;

    // Wait for the extra categories so the form can build their initial values
    // before mounting, preventing a reset that would discard edits.
    if (extraCategoriesLoading) return <Loader />;

    return (
        <FormComponent
            certificate={certificate}
            onClose={onClose}
            onCreate={handleCreateCertificate}
            onDelete={
                certificate && canUpdate ? handleDeleteCertificate : undefined
            }
            onUpdate={handleUpdateCertificate}
        />
    );
};
